import { apiClient, invalidateCache } from './client';

/**
 * The client's automatic mutation invalidation only strips the LAST url
 * segment, so `PATCH|DELETE /vocabulary/items/:id` clears `*\/vocabulary/items*`
 * and leaves the lesson's own `GET /vocabulary/lessons/:id` cached for five
 * minutes — a deleted word would come back on the next mount. Clear the whole
 * vocabulary namespace explicitly after those two calls.
 */
function invalidateVocabularyCache() {
  invalidateCache('*/vocabulary/*');
}

/**
 * Vocabulary overview for a course (staff).
 * @param {string} courseId
 * @returns {Promise<{ courseId, totalLessons, totalItems, lessons: object[] }>}
 */
export async function getCourseVocabulary(courseId) {
  const { data } = await apiClient.get(`/vocabulary/courses/${courseId}`);
  return data;
}

/**
 * Vocabulary items of a single lesson.
 * @param {string} lessonId
 * @returns {Promise<{ lessonId, lessonTitle, total, items: object[], summary: object }>}
 */
export async function getLessonVocabulary(lessonId) {
  const { data } = await apiClient.get(`/vocabulary/lessons/${lessonId}`);
  return data;
}

/**
 * Replace the whole word list of a lesson (bulk import). Each item is paired with the
 * stored word it names by `id`, else the one with the same Russian term, and only the
 * columns the item sends are written (audio, stress, examples and grammar stay as
 * stored); stored words missing from the list are removed. The importer's "add" mode
 * sends the stored words too (see pages/CourseVocabulary/wordListImport.js).
 * @param {string} lessonId
 * @param {{ items: object[] }} body
 */
export async function replaceLessonVocabulary(lessonId, body) {
  const { data } = await apiClient.post(`/vocabulary/lessons/${lessonId}`, body);
  return data;
}

/**
 * Add a single word to a lesson.
 * @param {string} lessonId
 * @param {object} body - term, translationUz, translationEn, partOfSpeech, ...
 */
export async function addVocabularyItem(lessonId, body) {
  const { data } = await apiClient.post(`/vocabulary/lessons/${lessonId}/items`, body);
  return data;
}

/**
 * Persist a new word order for a lesson.
 * @param {string} lessonId
 * @param {string[]} itemIds - ids in their new order
 */
export async function reorderLessonVocabulary(lessonId, itemIds) {
  const { data } = await apiClient.post(`/vocabulary/lessons/${lessonId}/reorder`, { itemIds });
  return data;
}

/**
 * Update one word.
 * @param {string} itemId
 * @param {object} body - partial word payload
 */
export async function updateVocabularyItem(itemId, body) {
  const { data } = await apiClient.patch(`/vocabulary/items/${itemId}`, body);
  invalidateVocabularyCache();
  return data;
}

/**
 * Delete one word.
 * @param {string} itemId
 */
export async function deleteVocabularyItem(itemId) {
  await apiClient.delete(`/vocabulary/items/${itemId}`);
  invalidateVocabularyCache();
}

/**
 * Attach or replace a word's picture (an address from uploadPublicMedia('image')). The API has no
 * way to take a picture off a word.
 * @param {string} itemId
 * @param {string} imageUrl
 */
export async function setVocabularyItemImage(itemId, imageUrl) {
  const { data } = await apiClient.post(`/vocabulary/items/${itemId}/image`, { imageUrl });
  invalidateVocabularyCache();
  return data;
}
